import { CheckIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useRef, useState, type FC } from 'react';

interface SortByItem {
    id: string;
    name: string;
}

interface SortByProps {
    options: SortByItem[];
    onChange: (id: string) => void;
}

export const SortBy: FC<SortByProps> = ({ options, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentOption, setCurrentOption] = useState<SortByItem>(options[0]);
    const ref = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleMenu = () => {
        setIsOpen((prev) => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="relative inline-block" ref={ref}>
            <button type="button" onClick={toggleMenu}>
                <div className="rounded-md h-11 gap-1 border-gray-100 w-40 border-solid border-[1px] flex items-center justify-center hover:pointer hover:bg-gray-150">
                    <span className="text-sm text-gray-110">Sort By:</span>
                    <span className="text-sm text-white">{currentOption.name}</span>
                    {isOpen ? (
                        <ChevronUpIcon className="h-2.5 w-2.5 text-white" />
                    ) : (
                        <ChevronDownIcon className="h-2.5 w-2.5 text-white" />
                    )}
                </div>
            </button>

            {/* Dropdown Menu */}
            <div
                className={`absolute left-0 right-0 bg-gray-100 mt-1 shadow-xl rounded-md transition-all duration-100 ease-in-out
          ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                ref={menuRef}
            >
                {options.map((option) => {
                    const isLastOption = options.length - 1 === options.indexOf(option);
                    return (
                        <div className="flex flex-col" key={option.id}>
                            <button
                                type="button"
                                className={`flex flex-row gap-2 items-center w-full px-4 py-2 text-white text-xs hover:bg-gray-80 hover:font-semibold rounded-md text-left ${option.id === currentOption.id ? 'font-semibold' : ''}`}
                                onClick={() => {
                                    onChange(option.id);
                                    setCurrentOption(option);
                                    toggleMenu();
                                }}
                            >
                                {option.name}{' '}
                                {option.id === currentOption.id && <CheckIcon className="h-3 w-3 text-white" />}
                            </button>
                            {!isLastOption && <hr className="bg-gray-70 border-gray-70  opacity-20 mt-1" />}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
